// The verdict strip — row one of the room: NUMBER (ink hero) → MEANING →
// ACTION, role-TRUE by contract:
//   · body shop  → the confirm queue + the ONLY primary button on the strip
//   · service    → team pulse; aging framed as delegation; quiet nav link
//   · director   → executive readout + oversight line; text links only —
//                  nothing styled as a to-do (flagged twice; never again)
// Meaning lines anchor to their evidence chapter, so the strip doubles as a
// table of contents by meaning.

import { useMemo } from "react"
import { Link } from "react-router"
import { ArrowRight, ArrowUp, ArrowDown, CalendarDate } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"
import { useOrganization } from "@/lib/organization/organization-context"
import { useAppointments } from "@/lib/api/use-appointments"
import {
  CHANNEL_SPARK, HEADLINE, INDUSTRY_CLOSE_RATE, INK, MINT, MINT_PALE, SOURCES,
  fmt$, type ChapterId, type SourceKey, type ViewMode,
} from "./room-model"
import type { Economics, Headline, deriveProfit } from "./derive"

type Profit = ReturnType<typeof deriveProfit>

export function VerdictStrip({ viewMode, d, profit, allChannels, rangeLabel, agingCount, aging48Count, agingValue, pendingCount, activity, quietCount, economics, onGo }: {
  viewMode: ViewMode
  d: Headline
  profit: Profit
  allChannels: boolean
  rangeLabel: string
  agingCount: number
  aging48Count: number
  agingValue: number
  pendingCount: number
  activity: { today: number; week: number }
  quietCount: number
  economics: Economics | null
  onGo: (chapter: ChapterId) => void
}) {
  const attention = agingCount > 0
  const closeDiff = d.closeRate - INDUSTRY_CLOSE_RATE

  // Today's arrivals from the booking calendar (Phase 4 connective tissue) —
  // the shop's morning question, answered on the strip.
  const { activeTeamId } = useOrganization()
  const todayRange = useMemo(() => {
    const start = new Date()
    start.setHours(0, 0, 0, 0)
    const end = new Date(start)
    end.setHours(23, 59, 59, 999)
    return { from: start.toISOString(), to: end.toISOString() }
  }, [])
  const { data: todaysAppts = [] } = useAppointments(
    activeTeamId,
    todayRange.from,
    todayRange.to,
  )
  const arrivingValue = todaysAppts.reduce((s, a) => s + (a.estimateValue || 0), 0)

  const quietLink = (label: string, chapter: ChapterId) => (
    <button onClick={() => onGo(chapter)} className="underline underline-offset-2 decoration-[#D6D3C8] hover:decoration-[#0F6E56] hover:text-zinc-900 transition-colors">
      {label}
    </button>
  )

  return (
    <div className="animate-element mb-4 grid grid-cols-1 lg:grid-cols-[minmax(300px,2fr)_3fr] gap-3">
      {/* NUMBER — the ink hero (the product's signature) */}
      <div className="relative rounded-2xl p-5 overflow-hidden flex flex-col justify-between min-h-[150px]" style={{ background: INK }}>
        <div className="absolute inset-x-0 top-0 h-[3px]" style={{ background: `linear-gradient(90deg, #1D9E75, ${MINT})` }} />
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: MINT_PALE }}>Identified opportunity</p>
            <p className="text-4xl font-extrabold tabular-nums mt-1.5 leading-none tracking-tight" style={{ ...HEADLINE, color: MINT }}>{fmt$(d.identified)}</p>
            <p className="text-[11px] mt-1.5" style={{ color: "rgba(232,248,242,0.55)" }}>
              <span className="tabular-nums" style={{ color: "#7EE0BD" }}>{fmt$(profit.total)}</span> est. gross profit · last {rangeLabel}
            </p>
          </div>
          {d.hasPrior && (
            <span className="flex items-center gap-0.5 text-[11px] font-semibold shrink-0 mt-0.5 text-[#7EE0BD]">
              {d.identifiedTrend >= 0 ? <ArrowUp className="size-3" aria-hidden /> : <ArrowDown className="size-3" aria-hidden />}
              {Math.abs(d.identifiedTrend)}%
            </span>
          )}
        </div>
        {/* Static channel-stacked spark — plain divs, no per-segment motion
            (an animated variant thrashed layout indefinitely). */}
        <div>
          <div className="flex items-end gap-[3px] h-8 mt-3 mb-1.5" aria-hidden>
            {d.heroTotals.map((total, i) => {
              const hMax = Math.max(...d.heroTotals, 1)
              const colH = Math.max((total / hMax) * 100, total > 0 ? 8 : 4)
              return (
                <div key={`${rangeLabel}-${i}`} className="flex-1 flex flex-col-reverse rounded-sm overflow-hidden" style={{ height: `${colH}%`, opacity: total > 0 ? 1 : 0.25, background: total > 0 ? undefined : "rgba(159,225,203,0.25)" }}>
                  {allChannels
                    ? d.heroStack.map(h => total > 0 && h.values[i] > 0 ? (
                        <div key={h.key} style={{ background: CHANNEL_SPARK[h.key as SourceKey], height: `${(h.values[i] / total) * 100}%` }} />
                      ) : null)
                    : total > 0 && <div style={{ background: i === d.heroTotals.length - 1 ? MINT : "rgba(159,225,203,0.35)", height: "100%" }} />}
                </div>
              )
            })}
          </div>
          {allChannels && (
            <div className="flex gap-2.5" aria-hidden>
              {SOURCES.map(s => (
                <span key={s.key} className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-emerald-100/50">
                  <span className="size-1.5 rounded-full" style={{ background: CHANNEL_SPARK[s.key] }} />
                  {s.label.split(" ")[0]}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* MEANING + ACTION — role-true panel */}
      <div className="rounded-2xl border border-[#ECEBE6] bg-white p-5 flex flex-col justify-center">
        {viewMode === "full" ? (
          <>
            <p className="text-base font-bold text-zinc-900" style={HEADLINE}>
              eTX found {fmt$(d.identified)} · captured {economics ? fmt$(economics.capturedMTD) : "—"} this month
            </p>
            <p className="text-sm text-[#6E6C64] mt-1">
              {d.closeRate}% close rate · <span className={closeDiff >= 0 ? "text-[#0F6E56] font-medium" : "text-[#BA7517] font-medium"}>{Math.abs(closeDiff)} pts {closeDiff >= 0 ? "ahead of" : "behind"} the {INDUSTRY_CLOSE_RATE}% benchmark</span> — {quietLink("see the pipeline", "converting")}
            </p>
            {attention && (
              <p className="text-[13px] text-[#6E6C64] mt-3 pt-3 border-t border-[#ECEBE6]">
                The shop is sitting on <strong className="text-zinc-800 tabular-nums">{agingCount}</strong> unworked estimate{agingCount !== 1 ? "s" : ""} (<strong className="tabular-nums text-zinc-800">{fmt$(agingValue)}</strong>) past 24h — {quietLink("who's working them", "people")}
              </p>
            )}
          </>
        ) : viewMode === "service" ? (
          <>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <p className="text-base font-bold text-zinc-900" style={HEADLINE}>
                {activity.today} scanned today · {activity.week} this week{quietCount > 0 && <> · <span className="text-[#BA7517]">{quietCount} advisor{quietCount !== 1 ? "s" : ""} quiet</span></>}
              </p>
              <button onClick={() => onGo("people")}
                className="shrink-0 inline-flex items-center gap-1.5 h-9 px-4 rounded-xl border border-[#E3E1DA] text-sm font-medium text-zinc-700 hover:border-[#0F6E56] hover:text-[#0F6E56] transition-colors">
                See advisors <ArrowRight className="size-3.5" aria-hidden />
              </button>
            </div>
            <p className="text-sm text-[#6E6C64] mt-1">
              {quietCount > 0 ? "Someone hasn't scanned this week — that's found money walking past." : "The whole team is scanning. Keep it rolling."}
            </p>
            {attention && (
              <p className="text-[13px] text-[#6E6C64] mt-3 pt-3 border-t border-[#ECEBE6]">
                <strong className="text-zinc-800 tabular-nums">{agingCount}</strong> estimate{agingCount !== 1 ? "s" : ""} waiting on the shop past 24h (<strong className="tabular-nums text-zinc-800">{fmt$(agingValue)}</strong>) — {quietLink("see the queue", "converting")}
              </p>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <p className="text-base font-bold text-zinc-900" style={HEADLINE}>
                {attention ? `${pendingCount} estimate${pendingCount !== 1 ? "s" : ""} to confirm` : "You're clear — nothing aging"}
              </p>
              {attention && (
                <Link to="/estimates?status=Pending&layout=pipeline"
                  className="shrink-0 inline-flex items-center gap-1.5 h-9 px-4 rounded-xl bg-zinc-900 text-white text-sm font-medium hover:bg-black transition-colors">
                  Review &amp; confirm <ArrowRight className="size-3.5" aria-hidden />
                </Link>
              )}
            </div>
            <p className="text-sm text-[#6E6C64] mt-1">
              {attention
                ? <>{agingCount} aging past 24h{aging48Count > 0 && <> · {aging48Count} past 48h</>} · <span className="font-semibold text-zinc-800 tabular-nums">{fmt$(agingValue)}</span> at risk of walking out</>
                : "No open estimates past 24h. Confirm new ones as they land."}
            </p>
            <p className="text-[13px] text-[#6E6C64] mt-3 pt-3 border-t border-[#ECEBE6]">
              {activity.today} new estimate{activity.today !== 1 ? "s" : ""} arrived today · {activity.week} this week — {quietLink("where the money sits", "converting")}
            </p>
            {todaysAppts.length > 0 && (
              <p className="text-[13px] text-[#6E6C64] mt-1.5 flex items-center gap-1.5 flex-wrap">
                <CalendarDate className="size-3.5 text-sky-600 shrink-0" aria-hidden />
                <span className="font-semibold text-zinc-800 tabular-nums">{todaysAppts.length}</span>
                car{todaysAppts.length !== 1 ? "s" : ""} arriving today
                {arrivingValue > 0 && (
                  <>
                    {" · "}
                    <span className="font-semibold text-zinc-800 tabular-nums">{fmt$(arrivingValue)}</span>
                  </>
                )}
                {" — "}
                <Link
                  to="/calendar"
                  className="underline underline-offset-2 decoration-[#D6D3C8] hover:decoration-[#0F6E56] hover:text-zinc-900 transition-colors"
                >
                  the calendar
                </Link>
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}
